import React, {
  useState,
  useEffect,
} from 'react';
import {
  useNavigate,
} from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Alert,
  CircularProgress,
  Chip,
  Grid,
  Card,
  CardContent,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Autocomplete,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Event as EventIcon,
  Refresh as RefreshIcon,
  Visibility as ViewIcon,
  People as PeopleIcon,
  CalendarToday as CalendarIcon,
  PersonAdd as PersonAddIcon,
  GridOn as GridIcon,
} from '@mui/icons-material';
import {
  eventsService,
} from '../../services/events';
import {
  apiService,
} from '../../services/api';

const EventManagement = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  
  // State
  const [events, setEvents] = useState([]);
  const [stats, setStats] = useState({
    total: 0,
    upcoming: 0,
    past: 0,
    totalParticipants: 0
  });

  // Participant management dialog state
  const [participantDialog, setParticipantDialog] = useState({
    open: false,
    event: null,
    participants: [],
    allUsers: [],
    selectedUser: null,
    loadingUsers: false,
    loadingAction: false
  });

  // Seatmap management dialog state
  const [seatmapDialog, setSeatmapDialog] = useState({
    open: false,
    event: null,
    participants: [],
    seatplan: null,
    rows: 0,
    columns: 0,
    loading: false,
    loadingAction: false
  });

  useEffect(() => {
    loadEvents();
  }, []);

  const loadEvents = async () => {
    setLoading(true);
    setError(null);

    try {
      const result = await eventsService.getEvents();
      
      if (result.success) {
        setEvents(result.data);
        
        // Calculate statistics
        const now = new Date();
        const upcoming = result.data.filter(e => new Date(e.start_date) > now).length;
        const past = result.data.filter(e => new Date(e.end_date) < now).length;
        const totalParticipants = result.data.reduce((sum, e) => sum + (e.participants?.length || 0), 0);
        
        setStats({
          total: result.data.length,
          upcoming,
          past,
          totalParticipants
        });
      } else {
        setError(result.error);
      }
    } catch (err) {
      setError('Failed to load events');
      console.error('Events loading error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleManageParticipants = async (event) => {
    setParticipantDialog({
      ...participantDialog,
      open: true,
      event: event,
      loadingUsers: true
    });

    // Load event participants
    const participantsResult = await eventsService.getEventParticipants(event._id || event.id);
    
    // Load all users
    const usersResult = await apiService.getUsers();

    setParticipantDialog(prev => ({
      ...prev,
      participants: participantsResult.success ? participantsResult.data : [],
      allUsers: usersResult.success ? usersResult.data : [],
      loadingUsers: false
    }));
  };

  const handleAddParticipant = async () => {
    if (!participantDialog.selectedUser) return;

    setParticipantDialog(prev => ({ ...prev, loadingAction: true }));

    const result = await eventsService.addEventParticipant(
      participantDialog.event._id || participantDialog.event.id,
      participantDialog.selectedUser._id || participantDialog.selectedUser.id
    );

    if (result.success) {
      setSuccess('Participant added successfully');
      // Reload participants
      const participantsResult = await eventsService.getEventParticipants(
        participantDialog.event._id || participantDialog.event.id
      );
      setParticipantDialog(prev => ({
        ...prev,
        participants: participantsResult.success ? participantsResult.data : prev.participants,
        selectedUser: null,
        loadingAction: false
      }));
      // Reload events to update stats
      loadEvents();
      setTimeout(() => setSuccess(null), 3000);
    } else {
      setError(result.error);
      setParticipantDialog(prev => ({ ...prev, loadingAction: false }));
      setTimeout(() => setError(null), 5000);
    }
  };

  const handleRemoveParticipant = async (userId) => {
    if (!window.confirm('Are you sure you want to remove this participant?')) {
      return;
    }

    setParticipantDialog(prev => ({ ...prev, loadingAction: true }));

    const result = await eventsService.removeEventParticipant(
      participantDialog.event._id || participantDialog.event.id,
      userId
    );

    if (result.success) {
      setSuccess('Participant removed successfully');
      // Reload participants
      const participantsResult = await eventsService.getEventParticipants(
        participantDialog.event._id || participantDialog.event.id
      );
      setParticipantDialog(prev => ({
        ...prev,
        participants: participantsResult.success ? participantsResult.data : prev.participants,
        loadingAction: false
      }));
      // Reload events to update stats
      loadEvents();
      setTimeout(() => setSuccess(null), 3000);
    } else {
      setError(result.error);
      setParticipantDialog(prev => ({ ...prev, loadingAction: false }));
      setTimeout(() => setError(null), 5000);
    }
  };

  const closeParticipantDialog = () => {
    setParticipantDialog({
      open: false,
      event: null,
      participants: [],
      allUsers: [],
      selectedUser: null,
      loadingUsers: false,
      loadingAction: false
    });
  };

  // Seatmap Management Handlers
  const handleManageSeatmap = async (event) => {
    setSeatmapDialog({
      ...seatmapDialog,
      open: true,
      event: event,
      loading: true
    });

    // Load event participants and seatplan
    const participantsResult = await eventsService.getEventParticipants(event._id || event.id);
    const eventResult = await eventsService.getEvent(event._id || event.id);

    if (eventResult.success && eventResult.data) {
      setSeatmapDialog(prev => ({
        ...prev,
        participants: participantsResult.success ? participantsResult.data : [],
        seatplan: eventResult.data.seatplan,
        rows: eventResult.data.seatplan.rows,
        columns: eventResult.data.seatplan.columns,
        loading: false
      }));
    } else {
      setError('Failed to load seatmap data');
      setSeatmapDialog(prev => ({ ...prev, loading: false }));
    }
  };

  const handleUpdateDimensions = async () => {
    if (!seatmapDialog.rows || !seatmapDialog.columns) {
      setError('Please enter valid dimensions');
      return;
    }

    setSeatmapDialog(prev => ({ ...prev, loadingAction: true }));

    const result = await eventsService.updateSeatplanDimensions(
      seatmapDialog.event._id || seatmapDialog.event.id,
      seatmapDialog.rows,
      seatmapDialog.columns
    );

    if (result.success) {
      setSuccess('Seatmap dimensions updated successfully');
      // Reload event data
      const eventResult = await eventsService.getEvent(seatmapDialog.event._id || seatmapDialog.event.id);
      if (eventResult.success) {
        setSeatmapDialog(prev => ({
          ...prev,
          seatplan: eventResult.data.seatplan,
          loadingAction: false
        }));
      }
      loadEvents();
      setTimeout(() => setSuccess(null), 3000);
    } else {
      setError(result.error);
      setSeatmapDialog(prev => ({ ...prev, loadingAction: false }));
      setTimeout(() => setError(null), 5000);
    }
  };

  const handleAssignSeat = async (row, col, userId) => {
    setSeatmapDialog(prev => ({ ...prev, loadingAction: true }));

    const result = await eventsService.assignSeatAdmin(
      seatmapDialog.event._id || seatmapDialog.event.id,
      userId,
      row,
      col
    );

    if (result.success) {
      setSuccess('Seat assigned successfully');
      // Reload seatplan
      const eventResult = await eventsService.getEvent(seatmapDialog.event._id || seatmapDialog.event.id);
      if (eventResult.success) {
        setSeatmapDialog(prev => ({
          ...prev,
          seatplan: eventResult.data.seatplan,
          loadingAction: false
        }));
      }
      setTimeout(() => setSuccess(null), 3000);
    } else {
      setError(result.error);
      setSeatmapDialog(prev => ({ ...prev, loadingAction: false }));
      setTimeout(() => setError(null), 5000);
    }
  };

  const handleClearAssignment = async (userId) => {
    setSeatmapDialog(prev => ({ ...prev, loadingAction: true }));

    const result = await eventsService.removeSeatAssignmentAdmin(
      seatmapDialog.event._id || seatmapDialog.event.id,
      userId
    );

    if (result.success) {
      setSuccess('Seat assignment cleared');
      // Reload seatplan
      const eventResult = await eventsService.getEvent(seatmapDialog.event._id || seatmapDialog.event.id);
      if (eventResult.success) {
        setSeatmapDialog(prev => ({
          ...prev,
          seatplan: eventResult.data.seatplan,
          loadingAction: false
        }));
      }
      setTimeout(() => setSuccess(null), 3000);
    } else {
      setError(result.error);
      setSeatmapDialog(prev => ({ ...prev, loadingAction: false }));
      setTimeout(() => setError(null), 5000);
    }
  };

  const handleToggleHideSeat = async (seatCoord) => {
    const isHidden = seatmapDialog.seatplan.hidden_seats.includes(seatCoord);
    setSeatmapDialog(prev => ({ ...prev, loadingAction: true }));

    const result = await eventsService.bulkHideSeats(
      seatmapDialog.event._id || seatmapDialog.event.id,
      [seatCoord],
      !isHidden
    );

    if (result.success) {
      setSuccess(isHidden ? 'Seat unhidden' : 'Seat hidden');
      // Reload seatplan
      const eventResult = await eventsService.getEvent(seatmapDialog.event._id || seatmapDialog.event.id);
      if (eventResult.success) {
        setSeatmapDialog(prev => ({
          ...prev,
          seatplan: eventResult.data.seatplan,
          loadingAction: false
        }));
      }
      setTimeout(() => setSuccess(null), 3000);
    } else {
      setError(result.error);
      setSeatmapDialog(prev => ({ ...prev, loadingAction: false }));
      setTimeout(() => setError(null), 5000);
    }
  };

  const closeSeatmapDialog = () => {
    setSeatmapDialog({
      open: false,
      event: null,
      participants: [],
      seatplan: null,
      rows: 0,
      columns: 0,
      loading: false,
      loadingAction: false
    });
  };

  const handleCreateEvent = () => {
    navigate('/create-event');
  };

  const handleEditEvent = (eventId) => {
    navigate(`/events/${eventId}/edit`);
  };

  const handleViewEvent = (eventId) => {
    navigate(`/events/${eventId}`);
  };

  const handleDeleteEvent = async (eventId) => {
    if (!window.confirm('Are you sure you want to delete this event? This action cannot be undone.')) {
      return;
    }

    try {
      const result = await eventsService.deleteEvent(eventId);
      
      if (result.success) {
        setSuccess('Event deleted successfully');
        loadEvents();
        setTimeout(() => setSuccess(null), 3000);
      } else {
        setError(result.error);
        setTimeout(() => setError(null), 5000);
      }
    } catch (err) {
      setError('Failed to delete event');
      console.error('Event deletion error:', err);
      setTimeout(() => setError(null), 5000);
    }
  };

  const getEventStatus = (event) => {
    const now = new Date();
    const start = new Date(event.start_date);
    const end = new Date(event.end_date);

    if (now < start) {
      return { label: 'Upcoming', color: 'primary' };
    }
    if (now > end) {
      return { label: 'Past', color: 'default' };
    }
    return { label: 'Ongoing', color: 'success' };
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric' 
    });
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h5" gutterBottom sx={{ fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: 1 }}>
            <EventIcon /> Event Management
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Create, edit, and manage all gaming events
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={loadEvents}
          >
            Refresh
          </Button>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleCreateEvent}
            color="primary"
          >
            Create Event
          </Button>
        </Box>
      </Box>

      {/* Success/Error Messages */}
      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}
      
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Statistics Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography variant="h4" sx={{ fontWeight: 'bold', color: 'primary.main' }}>
                    {stats.total}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Total Events
                  </Typography>
                </Box>
                <EventIcon sx={{ fontSize: 40, color: 'primary.main', opacity: 0.3 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography variant="h4" sx={{ fontWeight: 'bold', color: 'success.main' }}>
                    {stats.upcoming}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Upcoming Events
                  </Typography>
                </Box>
                <CalendarIcon sx={{ fontSize: 40, color: 'success.main', opacity: 0.3 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography variant="h4" sx={{ fontWeight: 'bold', color: 'text.secondary' }}>
                    {stats.past}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Past Events
                  </Typography>
                </Box>
                <EventIcon sx={{ fontSize: 40, color: 'text.secondary', opacity: 0.3 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography variant="h4" sx={{ fontWeight: 'bold', color: 'secondary.main' }}>
                    {stats.totalParticipants}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Total Participants
                  </Typography>
                </Box>
                <PeopleIcon sx={{ fontSize: 40, color: 'secondary.main', opacity: 0.3 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Events Table */}
      <Paper>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell><strong>Event Name</strong></TableCell>
                <TableCell><strong>Date</strong></TableCell>
                <TableCell><strong>Location</strong></TableCell>
                <TableCell><strong>Participants</strong></TableCell>
                <TableCell><strong>Status</strong></TableCell>
                <TableCell align="right"><strong>Actions</strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {events.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                    <Typography variant="body1" color="text.secondary">
                      No events found. Create your first event to get started!
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                events.map((event) => {
                  const status = getEventStatus(event);
                  return (
                    <TableRow key={event._id || event.id} hover>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          {event.name}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {event.start_date ? `${formatDate(event.start_date)} – ${formatDate(event.end_date)}` : '—'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {event.location}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip 
                          label={`${event.participants?.length || 0} / ${event.max_participants}`}
                          size="small"
                          color={event.participants?.length >= event.max_participants ? 'error' : 'default'}
                        />
                      </TableCell>
                      <TableCell>
                        <Chip 
                          label={status.label}
                          size="small"
                          color={status.color}
                        />
                      </TableCell>
                      <TableCell align="right">
                        <Tooltip title="Manage Participants">
                          <IconButton 
                            size="small" 
                            onClick={() => handleManageParticipants(event)}
                            color="secondary"
                          >
                            <PersonAddIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Manage Seatmap">
                          <IconButton 
                            size="small" 
                            onClick={() => handleManageSeatmap(event)}
                            color="primary"
                          >
                            <GridIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="View Event">
                          <IconButton 
                            size="small" 
                            onClick={() => handleViewEvent(event._id || event.id)}
                            color="info"
                          >
                            <ViewIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Edit Event">
                          <IconButton 
                            size="small" 
                            onClick={() => handleEditEvent(event._id || event.id)}
                            color="primary"
                          >
                            <EditIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete Event">
                          <IconButton 
                            size="small" 
                            onClick={() => handleDeleteEvent(event._id || event.id)}
                            color="error"
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Manage Participants Dialog */}
      <Dialog 
        open={participantDialog.open} 
        onClose={closeParticipantDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <PeopleIcon />
            Manage Participants - {participantDialog.event?.name}
          </Box>
        </DialogTitle>
        <DialogContent>
          {participantDialog.loadingUsers ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
              <CircularProgress />
            </Box>
          ) : (
            <Box>
              {/* Add Participant Section */}
              <Box sx={{ mb: 3, mt: 1 }}>
                <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 'bold' }}>
                  Add Participant
                </Typography>
                <Box sx={{ display: 'flex', gap: 2 }}>
                  <Autocomplete
                    fullWidth
                    options={participantDialog.allUsers.filter(user => 
                      !participantDialog.participants.some(p => (p.user_id || p.id) === (user._id || user.id))
                    )}
                    getOptionLabel={(option) => `${option.username} (${option.email})`}
                    value={participantDialog.selectedUser}
                    onChange={(event, newValue) => {
                      setParticipantDialog(prev => ({ ...prev, selectedUser: newValue }));
                    }}
                    renderInput={(params) => (
                      <TextField {...params} label="Select User" variant="outlined" />
                    )}
                    disabled={participantDialog.loadingAction}
                  />
                  <Button
                    variant="contained"
                    startIcon={<PersonAddIcon />}
                    onClick={handleAddParticipant}
                    disabled={!participantDialog.selectedUser || participantDialog.loadingAction}
                  >
                    Add
                  </Button>
                </Box>
              </Box>

              {/* Current Participants List */}
              <Box>
                <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 'bold' }}>
                  Current Participants ({participantDialog.participants.length})
                </Typography>
                {participantDialog.participants.length === 0 ? (
                  <Alert severity="info">No participants yet</Alert>
                ) : (
                  <Paper variant="outlined">
                    <List>
                      {participantDialog.participants.map((participant, index) => (
                        <React.Fragment key={participant.user_id || participant.id}>
                          <ListItem>
                            <ListItemText
                              primary={participant.username}
                              secondary={participant.email}
                            />
                            <ListItemSecondaryAction>
                              <IconButton
                                edge="end"
                                onClick={() => handleRemoveParticipant(participant.user_id || participant.id)}
                                disabled={participantDialog.loadingAction}
                                color="error"
                              >
                                <DeleteIcon />
                              </IconButton>
                            </ListItemSecondaryAction>
                          </ListItem>
                          {index < participantDialog.participants.length - 1 && <Box component="hr" sx={{ border: 'none', borderTop: '1px solid', borderColor: 'divider', m: 0 }} />}
                        </React.Fragment>
                      ))}
                    </List>
                  </Paper>
                )}
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeParticipantDialog}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Manage Seatmap Dialog */}
      <Dialog 
        open={seatmapDialog.open} 
        onClose={closeSeatmapDialog}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <GridIcon />
            Manage Seatmap - {seatmapDialog.event?.name}
          </Box>
        </DialogTitle>
        <DialogContent>
          {seatmapDialog.loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
              <CircularProgress />
            </Box>
          ) : (
            <Box>
              {/* Grid Dimensions Section */}
              <Box sx={{ mb: 3, mt: 1, p: 2, bgcolor: 'background.default', borderRadius: 1 }}>
                <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 'bold' }}>
                  Grid Dimensions
                </Typography>
                <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                  <TextField
                    label="Rows"
                    type="number"
                    value={seatmapDialog.rows}
                    onChange={(e) => setSeatmapDialog(prev => ({ ...prev, rows: parseInt(e.target.value) || 0 }))}
                    InputProps={{ inputProps: { min: 1, max: 50 } }}
                    sx={{ width: 120 }}
                    disabled={seatmapDialog.loadingAction}
                  />
                  <TextField
                    label="Columns"
                    type="number"
                    value={seatmapDialog.columns}
                    onChange={(e) => setSeatmapDialog(prev => ({ ...prev, columns: parseInt(e.target.value) || 0 }))}
                    InputProps={{ inputProps: { min: 1, max: 50 } }}
                    sx={{ width: 120 }}
                    disabled={seatmapDialog.loadingAction}
                  />
                  <Button
                    variant="contained"
                    onClick={handleUpdateDimensions}
                    disabled={seatmapDialog.loadingAction || !seatmapDialog.rows || !seatmapDialog.columns}
                  >
                    Update Dimensions
                  </Button>
                </Box>
                {seatmapDialog.seatplan && (
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                    Current: {seatmapDialog.seatplan.rows} rows × {seatmapDialog.seatplan.columns} columns 
                    ({seatmapDialog.seatplan.rows * seatmapDialog.seatplan.columns} total seats)
                  </Typography>
                )}
              </Box>

              {/* Seat Assignments Section */}
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 'bold' }}>
                  Seat Map
                </Typography>
                {seatmapDialog.seatplan && seatmapDialog.seatplan.rows * seatmapDialog.seatplan.columns > 0 ? (
                  <Box>
                    {/* Legend */}
                    <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Box sx={{ width: 20, height: 20, bgcolor: 'success.light', border: '1px solid', borderColor: 'success.main', borderRadius: 0.5 }} />
                        <Typography variant="caption">Assigned</Typography>
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Box sx={{ width: 20, height: 20, bgcolor: 'background.paper', border: '1px solid', borderColor: 'divider', borderRadius: 0.5 }} />
                        <Typography variant="caption">Available</Typography>
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Box sx={{ width: 20, height: 20, bgcolor: 'action.disabledBackground', border: '1px solid', borderColor: 'action.disabled', borderRadius: 0.5 }} />
                        <Typography variant="caption">Hidden</Typography>
                      </Box>
                    </Box>

                    {/* Seat Grid */}
                    <Paper variant="outlined" sx={{ p: 2, bgcolor: 'background.default', maxHeight: 500, overflow: 'auto' }}>
                      <Box sx={{ display: 'inline-block', minWidth: '100%' }}>
                        {Array.from({ length: seatmapDialog.seatplan.rows }).map((_, rowIdx) => {
                          const row = rowIdx + 1;
                          return (
                            <Box key={row} sx={{ display: 'flex', gap: 1, mb: 1, alignItems: 'center' }}>
                              {/* Row Label */}
                              <Typography 
                                variant="caption" 
                                sx={{ 
                                  width: 30, 
                                  textAlign: 'right', 
                                  fontWeight: 'bold',
                                  color: 'text.secondary'
                                }}
                              >
                                {row}
                              </Typography>
                              
                              {/* Seats in this row */}
                              {Array.from({ length: seatmapDialog.seatplan.columns }).map((_, colIdx) => {
                                const col = colIdx + 1;
                                const seatCoord = `${row}-${col}`;
                                const assignment = seatmapDialog.seatplan.assignments.find(a => a.row === row && a.column === col);
                                const isHidden = seatmapDialog.seatplan.hidden_seats.includes(seatCoord);
                                
                                return (
                                  <Tooltip 
                                    key={col}
                                    title={
                                      isHidden ? `Hidden - Row ${row}, Col ${col}` :
                                      assignment ? `${assignment.username} - Row ${row}, Col ${col}` :
                                      `Available - Row ${row}, Col ${col}`
                                    }
                                  >
                                    <Box
                                      sx={{
                                        width: 50,
                                        height: 50,
                                        display: 'flex',
                                        flexDirection: 'column',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        bgcolor: isHidden ? 'action.disabledBackground' : 
                                                assignment ? 'success.light' : 
                                                'background.paper',
                                        border: '2px solid',
                                        borderColor: isHidden ? 'action.disabled' :
                                                    assignment ? 'success.main' :
                                                    'divider',
                                        borderRadius: 1,
                                        cursor: 'pointer',
                                        transition: 'all 0.2s',
                                        '&:hover': {
                                          boxShadow: 2,
                                          borderColor: 'primary.main'
                                        }
                                      }}
                                      onClick={() => {
                                        // Show seat management options
                                        const availableParticipants = seatmapDialog.participants.filter(p => 
                                          !seatmapDialog.seatplan.assignments.some(a => a.user_id === (p.user_id || p.id))
                                        );
                                        
                                        if (assignment) {
                                          const action = window.confirm(
                                            `Seat ${row}-${col} is assigned to ${assignment.username}.\n\nClick OK to clear assignment, Cancel to hide/unhide seat.`
                                          );
                                          if (action) {
                                            handleClearAssignment(assignment.user_id);
                                          } else {
                                            handleToggleHideSeat(seatCoord);
                                          }
                                        } else if (isHidden) {
                                          const action = window.confirm(
                                            `Seat ${row}-${col} is hidden.\n\nClick OK to unhide.`
                                          );
                                          if (action) {
                                            handleToggleHideSeat(seatCoord);
                                          }
                                        } else {
                                          // Show dialog to assign or hide
                                          const action = window.prompt(
                                            `Seat ${row}-${col} options:\n\nEnter username to assign, or type "hide" to hide this seat.\n\nAvailable users: ${availableParticipants.map(p => p.username).join(', ')}`
                                          );
                                          if (action === 'hide') {
                                            handleToggleHideSeat(seatCoord);
                                          } else if (action) {
                                            const user = availableParticipants.find(p => p.username.toLowerCase() === action.toLowerCase());
                                            if (user) {
                                              handleAssignSeat(row, col, user.user_id || user.id);
                                            } else {
                                              alert('User not found or already assigned');
                                            }
                                          }
                                        }
                                      }}
                                    >
                                      <Typography 
                                        variant="caption" 
                                        sx={{ 
                                          fontSize: '0.65rem',
                                          fontWeight: 'bold',
                                          color: isHidden ? 'action.disabled' : 
                                                 assignment ? 'success.dark' : 
                                                 'text.secondary'
                                        }}
                                      >
                                        {col}
                                      </Typography>
                                      {assignment && (
                                        <Typography 
                                          variant="caption" 
                                          sx={{ 
                                            fontSize: '0.6rem',
                                            maxWidth: '100%',
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis',
                                            whiteSpace: 'nowrap',
                                            px: 0.5,
                                            color: 'success.dark'
                                          }}
                                        >
                                          {assignment.username.substring(0, 5)}
                                        </Typography>
                                      )}
                                      {isHidden && (
                                        <Typography variant="caption" sx={{ fontSize: '0.5rem', color: 'action.disabled' }}>
                                          X
                                        </Typography>
                                      )}
                                    </Box>
                                  </Tooltip>
                                );
                              })}
                            </Box>
                          );
                        })}
                        
                        {/* Column Labels */}
                        <Box sx={{ display: 'flex', gap: 1, mt: 1, alignItems: 'center' }}>
                          <Box sx={{ width: 30 }} />
                          {Array.from({ length: seatmapDialog.seatplan.columns }).map((_, colIdx) => (
                            <Typography 
                              key={colIdx + 1}
                              variant="caption" 
                              sx={{ 
                                width: 50, 
                                textAlign: 'center', 
                                fontWeight: 'bold',
                                color: 'text.secondary'
                              }}
                            >
                              {colIdx + 1}
                            </Typography>
                          ))}
                        </Box>
                      </Box>
                    </Paper>

                    <Alert severity="info" sx={{ mt: 2 }}>
                      Click on any seat to assign, clear, or hide it.
                    </Alert>
                  </Box>
                ) : (
                  <Alert severity="info">No seats configured. Update dimensions to create seats.</Alert>
                )}
              </Box>

              {/* Unassigned Participants Section */}
              <Box>
                <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 'bold' }}>
                  Unassigned Participants
                </Typography>
                {(() => {
                  const unassigned = seatmapDialog.participants.filter(p => 
                    !seatmapDialog.seatplan?.assignments.some(a => a.user_id === (p.user_id || p.id))
                  );
                  return unassigned.length === 0 ? (
                    <Alert severity="success">All participants have seat assignments!</Alert>
                  ) : (
                    <Paper variant="outlined">
                      <List>
                        {unassigned.map((participant, index) => (
                          <React.Fragment key={participant.user_id || participant.id}>
                            <ListItem>
                              <ListItemText
                                primary={participant.username}
                                secondary={participant.email}
                              />
                            </ListItem>
                            {index < unassigned.length - 1 && <Box component="hr" sx={{ border: 'none', borderTop: '1px solid', borderColor: 'divider', m: 0 }} />}
                          </React.Fragment>
                        ))}
                      </List>
                    </Paper>
                  );
                })()}
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeSeatmapDialog}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default EventManagement;
